import type { ReactNode } from 'react'
import styled, { css, useTheme } from 'styled-components'

import { colors } from 'bl-common/src/constants/colors'
import {
  Button as _Button,
  type ButtonPreset,
  type ButtonProps,
} from 'bl-common/src/elements/Button/Button'
import type { ScreenTheme } from 'bl-common/src/styles/types'
import { media } from 'bl-common/src/utils/media'

interface Props extends ButtonProps {
  screenTheme?: ScreenTheme
  isSubmit?: boolean
  isSticky?: boolean
  children: ReactNode
}

const themeToPresetsMap = {
  default: 'blue',
  blue: 'lightGrey',
  highlandDefault: 'highlandGreen',
  highlandGreen: 'highlandWhite',
}

const StyledButton = styled(_Button)<{ $isSticky?: boolean }>`
  ${({ $isSticky, theme }) =>
    $isSticky &&
    css`
      position: sticky;
      bottom: 24px;
      margin-top: auto;
      flex-shrink: 0;
      align-self: center;

      ${media.bmd(css`
        bottom: 0;
        margin-top: ${theme.spacing[3]};
      `)}
    `}
`

export const Button = ({
  screenTheme,
  preset,
  isSubmit = false,
  disabled,
  paddingSize = 'medium',
  onClick,
  isSticky,
  children,
  to,
  style,
}: Props) => {
  const theme = useTheme()
  const themeStyle = theme?.bookingEngine?.[screenTheme]?.buttonField

  const colorStyle: { color?: string } = {}
  const buttonPreset =
    preset ?? (themeToPresetsMap[screenTheme] as ButtonPreset)
  // For this scenario, we want to use the lightGrey preset for the button but change the color to the screenTheme color
  if (
    (screenTheme === 'blue' && buttonPreset === 'lightGrey') ||
    buttonPreset === 'white'
  ) {
    colorStyle.color = colors.deepBlue
  }
  if (screenTheme === 'retreat' && buttonPreset === 'white') {
    colorStyle.color = colors.darkmode
  }

  return (
    <StyledButton
      type={isSubmit === true ? 'submit' : 'button'}
      onClick={onClick}
      preset={buttonPreset}
      paddingSize={paddingSize}
      style={{
        width: '100%',
        ...themeStyle?.button,
        ...colorStyle,
        ...style,
      }}
      disabled={disabled}
      $isSticky={isSticky}
      to={to}
    >
      {children}
    </StyledButton>
  )
}
